// @catalog F.006
// StoresPOSTable + StoreAnalysisTable — estilo handoff con filtro por nombre +
// columnas sortables. Tabla compartida POS/vendedores SO.
import { useMemo } from "react";
import { Alert, Box, Stack } from "@mui/material";
import { Store, BusinessCenter } from "@mui/icons-material";
import AnalyticsService, {
  type StoreMetrics,
} from "../../services/analytics.service";
import {
  TableShell,
  HeaderCell,
  BodyRow,
  BodyCell,
  RankNum,
  DeltaPill,
  Tag,
} from "../analytics/TableCells";
import {
  SortableHeaderCell,
  TableFilterInput,
  useTableControls,
} from "../analytics/TableControls";
import { sumStoresTotalSales } from "./helpers";

interface StoreAnalysisTableProps {
  stores: StoreMetrics[];
}

export function StoreAnalysisTable({
  stores,
}: StoreAnalysisTableProps): JSX.Element {
  const totalSalesAllStores = useMemo(
    () => sumStoresTotalSales(stores ?? []),
    [stores],
  );

  const filterAccessor = useMemo(
    () => (s: StoreMetrics) => s.storeName,
    [],
  );
  const sortAccessors = useMemo(
    () => ({
      storeName: (s: StoreMetrics) => s.storeName,
      totalSales: (s: StoreMetrics) => s.totalSales,
      marketShare: (s: StoreMetrics) =>
        totalSalesAllStores > 0
          ? (s.totalSales / totalSalesAllStores) * 100
          : 0,
      unitsSold: (s: StoreMetrics) => s.unitsSold,
      ordersCount: (s: StoreMetrics) => s.ordersCount,
      growth: (s: StoreMetrics) =>
        s.growthPercentage ?? Number.NEGATIVE_INFINITY,
    }),
    [totalSalesAllStores],
  );

  const { filter, setFilter, sort, toggleSort, processedData } =
    useTableControls<StoreMetrics>({
      data: stores ?? [],
      filterAccessor,
      sortAccessors,
      defaultSort: { key: "totalSales", dir: "desc" },
    });

  if (!stores || stores.length === 0) {
    return (
      <Alert severity="info">
        No hay datos disponibles para el mes actual.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <TableFilterInput
        value={filter}
        onChange={setFilter}
        placeholder="Buscar tienda / vendedor…"
        helper={
          filter
            ? `${processedData.length} de ${stores.length}`
            : `${stores.length} tiendas/vendedores`
        }
      />
      {processedData.length === 0 ? (
        <Alert severity="info">Sin resultados para "{filter}".</Alert>
      ) : (
        <TableShell maxHeight={600}>
          <thead>
            <tr>
              <HeaderCell width={40} align="right">
                #
              </HeaderCell>
              <SortableHeaderCell
                sortKey="storeName"
                currentSort={sort}
                onSort={toggleSort}
              >
                Tienda / Vendedor
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="totalSales"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Ventas
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="marketShare"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                % Particip.
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="unitsSold"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Unidades
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="ordersCount"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Órdenes
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="growth"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
                tooltip="Crecimiento de ventas frente al mismo período del año anterior (Odoo 18 vs. Odoo 16)."
              >
                Crecimiento
              </SortableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {processedData.map((store, index) => {
              const marketShare =
                totalSalesAllStores > 0
                  ? (store.totalSales / totalSalesAllStores) * 100
                  : 0;
              const isVendor =
                store.storeType === "vendor" ||
                store.storeName.includes("(Vendedor)") ||
                store.storeName.includes("(Tienda)");
              const isLowPerf =
                store.growthPercentage !== undefined &&
                store.growthPercentage !== null &&
                store.growthPercentage < -10;
              return (
                <BodyRow key={store.storeName}>
                  <BodyCell align="right">
                    <RankNum n={index + 1} />
                  </BodyCell>
                  <BodyCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        minWidth: 0,
                      }}
                    >
                      {isVendor ? (
                        <BusinessCenter
                          sx={{ fontSize: 14, color: "var(--ink-3)" }}
                        />
                      ) : (
                        <Store sx={{ fontSize: 14, color: "var(--ink-3)" }} />
                      )}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 500,
                          color: "var(--ink-1)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {store.storeName}
                      </Box>
                      {isLowPerf && <Tag label="Caída" tone="alert" />}
                    </Box>
                  </BodyCell>
                  <BodyCell align="right">
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {AnalyticsService.formatCurrency(store.totalSales)}
                    </Box>
                  </BodyCell>
                  <BodyCell align="right" muted>
                    <Box
                      sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "4px",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {marketShare.toFixed(1)}%
                      </Box>
                      <Box
                        sx={{
                          width: 60,
                          height: 4,
                          backgroundColor: "var(--bg-2)",
                          borderRadius: "2px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(marketShare, 100)}%`,
                            height: "100%",
                            backgroundColor: "var(--accent)",
                            borderRadius: "2px",
                          }}
                        />
                      </Box>
                    </Box>
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {AnalyticsService.formatNumber(store.unitsSold)}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {AnalyticsService.formatNumber(store.ordersCount)}
                  </BodyCell>
                  <BodyCell align="right">
                    <DeltaPill delta={store.growthPercentage ?? null} />
                  </BodyCell>
                </BodyRow>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </Stack>
  );
}

interface StoresPOSTableProps {
  stores: StoreMetrics[];
}

export function StoresPOSTable({ stores }: StoresPOSTableProps): JSX.Element {
  return <StoreAnalysisTable stores={stores} />;
}
